// ---------------------------------------------------------------------------
// styleColors.js
// Gives each generated page its own colour scheme without touching the
// template's layout. Every colour literal inside the page's <style> blocks
// (#hex, rgb(), rgba()) is shifted by the same random hue and a small
// saturation change. Lightness and alpha are kept, so dark stays dark,
// light text stays light and contrast is preserved. Using one shift for all
// colours keeps the palette harmonious (accents still match each other).
// ---------------------------------------------------------------------------

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

const rgbToHsl = (r, g, b) => {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return [h * 60, s, l];
};

const hslToRgb = (h, s, l) => {
  h = ((h % 360) + 360) % 360 / 360;
  if (s === 0) { const v = Math.round(l * 255); return [v, v, v]; }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return [channel(h + 1 / 3), channel(h), channel(h - 1 / 3)].map((v) => Math.round(v * 255));
};

const hex2 = (n) => n.toString(16).padStart(2, '0');

/**
 * Recolour one CSS string.
 * @param {string} css
 * @param {{hueShift: number, satScale: number}} scheme
 */
const recolour = (css, { hueShift, satScale }) => {
  const shift = (r, g, b) => {
    const [h, s, l] = rgbToHsl(r, g, b);
    if (s < 0.04) return [r, g, b];              // true greys, black and white stay neutral
    return hslToRgb(h + hueShift, clamp(s * satScale, 0, 1), l);
  };
  return css
    .replace(/rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(,\s*[\d.]+\s*)?\)/gi, (whole, r, g, b, alpha) => {
      const [nr, ng, nb] = shift(Number(r), Number(g), Number(b));
      return alpha ? `rgba(${nr},${ng},${nb}${alpha.replace(/\s+/g, '')})` : `rgb(${nr},${ng},${nb})`;
    })
    .replace(/#([0-9a-f]{6}|[0-9a-f]{3})(?![0-9a-f])/gi, (whole, hex) => {
      const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
      const [nr, ng, nb] = shift(parseInt(full.slice(0, 2), 16), parseInt(full.slice(2, 4), 16), parseInt(full.slice(4, 6), 16));
      return `#${hex2(nr)}${hex2(ng)}${hex2(nb)}`;
    });
};

// Golden angle: stepping the hue by this amount per page keeps every page's
// hue different from all the others and far from its neighbours.
const GOLDEN_ANGLE = 137.50776405;

/**
 * Hue for page `index` of a batch: a random starting point for the batch,
 * then one golden-angle step per page. No two pages in a batch share a hue.
 */
export const batchHue = (batchStart, index) => Number(((batchStart + index * GOLDEN_ANGLE) % 360).toFixed(2));

/**
 * Give a page a unique colour scheme.
 * @param {string} html
 * @param {SeededRNG} rng
 * @param {number} [hue] - hue shift in degrees for this page (see batchHue);
 *                         a random one is used if omitted
 * @returns {{html: string, hueShift: number, count: number}}
 */
export const randomiseStyleColors = (html, rng, hue) => {
  const hueShift = Number.isFinite(hue) ? hue : 20 + Math.floor(rng._next() * 320);
  const satScale = 0.85 + rng._next() * 0.3;
  let count = 0;
  const out = html.replace(/(<style\b[^>]*>)([\s\S]*?)(<\/style>)/gi, (whole, open, css, close) => {
    count += (css.match(/#[0-9a-f]{3,6}(?![0-9a-f])|rgba?\(/gi) || []).length;
    return open + recolour(css, { hueShift, satScale }) + close;
  });
  return { html: out, hueShift, count };
};
